import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Trophy, Plus, Loader2, Users, CalendarDays, ListOrdered, Target, Check, X, ArrowLeft, Search, Trash2, Zap, Eraser, Layers } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { MatchdayShareBar, LeadersShareButton, WeeklyPackButton } from "../../components/StandingsShare";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty, GoldButton, GhostButton, Card } from "../../components/UI";
import { ClubCrest } from "../../components/ClubCrest";
import { toast } from "sonner";

const STATUS_STYLE = {
  DRAFT: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  SCHEDULED: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  ACTIVE: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${STATUS_STYLE[status] || "bg-slate-700/30 text-slate-300 border-slate-600"}`} data-testid={`comp-status-${status}`}>
      {status}
    </span>
  );
}

// ----------------------------------------------------------------- Create modal
function CreateModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [season, setSeason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (name.trim().length < 3 || !season.trim()) { toast.error("Name (3+ chars) and season label are required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/competitions", { name: name.trim(), season_label: season.trim() });
      toast.success("Competition created");
      onDone(data.competition.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="comp-create-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50">New Competition</h3>
        <p className="text-sm text-slate-400 mt-1">A single round-robin league. Scores are informational only and never affect ratings.</p>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-4">Competition name</label>
        <input data-testid="comp-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Aegean Invitational"
          className="w-full mt-1 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-3">Season label</label>
        <input data-testid="comp-season-input" value={season} onChange={(e) => setSeason(e.target.value)} maxLength={20} placeholder="e.g. 2026/27"
          className="w-full mt-1 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        <div className="flex justify-end gap-2 mt-5">
          <GhostButton onClick={onClose} data-testid="comp-create-cancel">Cancel</GhostButton>
          <GoldButton onClick={submit} disabled={busy} data-testid="comp-create-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create</GoldButton>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- List view
function ListView() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    setRows(null);
    try { const { data } = await api.get("/admin/competitions"); setRows(data.competitions || []); }
    catch (e) { toast.error(apiErr(e)); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-competitions-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Control Center</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none flex items-center gap-3">
            <Trophy className="w-8 h-8 text-[#E5B842] shrink-0" /> Competitions
          </h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">Build leagues, draw fixtures and keep standings. Scores are informational only.</p>
        </div>
        <GoldButton onClick={() => setCreating(true)} data-testid="comp-new-btn"><Plus className="w-4 h-4" /> New competition</GoldButton>
      </div>

      {rows === null ? <Loader label="Loading competitions…" /> : rows.length === 0 ? (
        <Empty label="No competitions yet. Create your first league." />
      ) : (
        <div className="grid gap-3" data-testid="comp-list">
          {rows.map((c) => (
            <Card key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-[#E5B842]/40 cursor-pointer" data-testid={`comp-card-${c.id}`} onClick={() => nav(`/admin/competitions/${c.id}`)} style={{ transition: "border-color 0.15s ease" }}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-base font-bold uppercase tracking-tight text-slate-50 break-words">{c.name}</span>
                  <StatusPill status={c.status} />
                </div>
                <div className="text-xs text-slate-400 font-mono mt-1">{c.season_label} · {c.club_count} club{c.club_count === 1 ? "" : "s"} · {c.matchdays} matchday{c.matchdays === 1 ? "" : "s"} · {c.fixtures_played}/{c.fixtures_total} played</div>
              </div>
              <GhostButton className="!py-1.5 text-xs shrink-0" data-testid={`comp-open-${c.id}`}>Manage</GhostButton>
            </Card>
          ))}
        </div>
      )}
      {creating && <CreateModal onClose={() => setCreating(false)} onDone={(id) => nav(`/admin/competitions/${id}`)} />}
    </div>
  );
}

// ----------------------------------------------------------------- Clubs tab
function ClubsTab({ comp, reload }) {
  const locked = comp.schedule_generated;
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = comp.clubs.map((c) => c.id);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`); setResults((data.clubs || []).filter((c) => c.status === "ACTIVE")); }
      catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (c) => {
    if (current.includes(c.id)) return;
    setBusy(true); setQ(""); setResults(null);
    try { await api.post(`/admin/competitions/${comp.id}/assign-club`, { club_id: c.id }); toast.success(`${c.name} assigned`); await reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/competitions/${comp.id}/clubs/${id}/remove`); toast.success("Club removed"); await reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="comp-tab-clubs">
      {locked && <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="clubs-locked">Clubs are locked — the schedule has been generated.</div>}
      {!locked && (
        <div className="relative mb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-2">Assign Club (direct)</div>
          <Search className="w-4 h-4 text-[#E5B842]/70 absolute left-3.5 top-9" />
          <input data-testid="clubs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ACTIVE clubs by name or WSTF ID"
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-11 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
          {results && results.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] divide-y divide-[rgba(212,175,55,0.1)] max-h-72 overflow-auto" data-testid="clubs-search-results">
              {results.map((c) => (
                <button key={c.id} onClick={() => add(c)} disabled={current.includes(c.id) || busy} data-testid={`clubs-add-${c.wsbf_club_id}`}
                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#161616] disabled:opacity-40">
                  <ClubCrest club={c} size={32} />
                  <div className="min-w-0"><div className="text-sm text-slate-100 truncate">{c.name}</div><div className="text-[11px] font-mono text-slate-500">{c.wsbf_club_id}</div></div>
                  {current.includes(c.id) && <span className="ml-auto text-[10px] text-emerald-300 font-mono">ADDED</span>}
                </button>
              ))}
            </div>
          )}
          {results && results.length === 0 && q.trim() && <div className="absolute z-10 left-0 right-0 mt-1 rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-3 text-xs text-slate-500">No ACTIVE clubs match “{q}”.</div>}
        </div>
      )}
      {comp.clubs.length === 0 ? <Empty label="No clubs added yet." /> : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="clubs-current">
          {comp.clubs.map((c) => (
            <Card key={c.id} className="p-3 flex items-center gap-3" data-testid={`clubs-row-${c.id}`}>
              <ClubCrest club={c} size={40} />
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-100 truncate">{c.name}</div><div className="text-[11px] font-mono text-slate-500">{c.wsbf_club_id}{c.city ? ` · ${c.city}` : ""}</div></div>
              {!locked && <button onClick={() => remove(c.id)} disabled={busy} data-testid={`clubs-remove-${c.id}`} className="text-rose-400 hover:text-rose-300 shrink-0 p-1" title="Remove"><Trash2 className="w-4 h-4" /></button>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- Schedule tab
function ScheduleTab({ comp, reload }) {
  const [busy, setBusy] = useState(false);
  const byMd = {};
  comp.fixtures.forEach((f) => { (byMd[f.matchday] = byMd[f.matchday] || []).push(f); });
  const mds = Object.keys(byMd).map(Number).sort((a, b) => a - b);
  const generate = async () => {
    if (!window.confirm(comp.schedule_generated ? "Regenerate the schedule? Existing fixtures will be replaced (only allowed while no scores are entered)." : "Generate the round-robin schedule now?")) return;
    setBusy(true);
    try { await api.post(`/admin/competitions/${comp.id}/generate-schedule`); toast.success("Schedule generated"); await reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="comp-tab-schedule">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-sm text-slate-400">{comp.clubs.length} clubs · single round-robin (everyone plays once).</div>
        <GoldButton onClick={generate} disabled={busy || comp.clubs.length < 2} data-testid="schedule-generate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {comp.schedule_generated ? "Regenerate" : "Generate schedule"}</GoldButton>
      </div>
      {comp.fixtures.length === 0 ? <Empty label="No fixtures yet. Add at least 2 clubs, then generate the schedule." /> : (
        <div className="space-y-5">
          {mds.map((md) => (
            <div key={md} data-testid={`schedule-matchday-${md}`}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-2">Matchday {md}</div>
              <Card className="divide-y divide-[rgba(212,175,55,0.1)]">
                {byMd[md].map((f) => (
                  <div key={f.id} className="flex items-center gap-2 p-3 text-sm" data-testid={`fixture-${f.id}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0 justify-end text-right"><span className="truncate text-slate-100">{f.home.name}</span><ClubCrest club={f.home} size={24} /></div>
                    <span className="font-mono text-slate-500 shrink-0 px-2">{f.status === "PLAYED" ? `${f.home_score}–${f.away_score}` : "vs"}</span>
                    <div className="flex items-center gap-2 flex-1 min-w-0"><ClubCrest club={f.away} size={24} /><span className="truncate text-slate-100">{f.away.name}</span></div>
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- Scores tab
function ScoreRow({ comp, f, reload }) {
  const [h, setH] = useState(f.home_score ?? "");
  const [a, setA] = useState(f.away_score ?? "");
  const [status, setStatus] = useState(f.status || "SCHEDULED");
  const [k, setK] = useState(kickVal(f.scheduled_date));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setH(f.home_score ?? ""); setA(f.away_score ?? ""); setStatus(f.status || "SCHEDULED"); setK(kickVal(f.scheduled_date));
  }, [f.home_score, f.away_score, f.status, f.scheduled_date]);
  const save = async () => {
    const body = { status };
    if (k !== kickVal(f.scheduled_date)) body.scheduled_date = k || null;
    if (status === "PLAYED") {
      if (h === "" || a === "" || +h < 0 || +a < 0) {
        toast.error("Enter valid scores (0 or more)."); return;
      }
      body.home_score = parseInt(h, 10);
      body.away_score = parseInt(a, 10);
    } else {
      // Clear scores when the fixture is not PLAYED.
      body.home_score = null; body.away_score = null;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/competitions/${comp.id}/fixtures/${f.id}`, body);
      toast.success("Saved — standings refreshed");
      await reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 p-3 flex-wrap" data-testid={`score-row-${f.id}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end text-right"><span className="truncate text-sm text-slate-100">{f.home.short_name || f.home.name}</span><ClubCrest club={f.home} size={24} /></div>
      <input data-testid={`score-home-${f.id}`} value={h} onChange={(e) => setH(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" disabled={status !== "PLAYED"} className="w-11 text-center bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-1 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60 disabled:opacity-40" />
      <span className="text-slate-600">–</span>
      <input data-testid={`score-away-${f.id}`} value={a} onChange={(e) => setA(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" disabled={status !== "PLAYED"} className="w-11 text-center bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-1 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60 disabled:opacity-40" />
      <div className="flex items-center gap-2 flex-1 min-w-0"><ClubCrest club={f.away} size={24} /><span className="truncate text-sm text-slate-100">{f.away.short_name || f.away.name}</span></div>
      <select data-testid={`score-status-${f.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="shrink-0 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1.5 text-[11px] font-mono uppercase text-slate-200 focus:outline-none focus:border-[#E5B842]/60">
        <option value="SCHEDULED">SCHEDULED</option>
        <option value="PLAYED">PLAYED</option>
        <option value="POSTPONED">POSTPONED</option>
        <option value="CANCELLED">CANCELLED</option>
      </select>
      <input type="datetime-local" data-testid={`score-kickoff-${f.id}`} value={k} onChange={(e) => setK(e.target.value)} title="Kick-off"
        className="shrink-0 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1 text-[11px] font-mono text-slate-200 [color-scheme:dark]" />
      <button onClick={save} disabled={busy} data-testid={`score-save-${f.id}`} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase bg-[#E5B842] text-black disabled:opacity-40">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Save</button>
      {f.status === "PLAYED" && <ClearResultButton comp={comp} f={f} reload={reload} />}
    </div>
  );
}

function ClearResultButton({ comp, f, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/competitions/${comp.id}/fixtures/${f.id}/clear`);
      if (data.noop) toast.info(data.message); else toast.success("Result cleared — standings refreshed");
      setOpen(false);
      await reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`score-clear-${f.id}`} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase border border-rose-500/40 text-rose-300 hover:bg-rose-950/40" style={{ transition: "background-color 0.15s ease" }}>
          <Eraser className="w-3.5 h-3.5" />Καθαρισμός
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 border-[rgba(212,175,55,0.35)] text-slate-200 shadow-2xl shadow-black/80" style={{ backgroundColor: "#0d0d0d" }} data-testid={`score-clear-confirm-${f.id}`}>
        <div className="text-sm font-semibold">Clear result {f.home_score}–{f.away_score}?</div>
        <div className="text-[11px] text-slate-400 mt-1">Fixture returns to SCHEDULED and standings are recomputed.</div>
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={() => setOpen(false)} data-testid={`score-clear-cancel-${f.id}`} className="px-2.5 py-1 rounded-md text-xs text-slate-300 border border-slate-700">Cancel</button>
          <button onClick={clear} disabled={busy} data-testid={`score-clear-yes-${f.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold uppercase bg-rose-500 text-black disabled:opacity-40">{busy && <Loader2 className="w-3 h-3 animate-spin" />}Clear</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ----------------------------------------------------------------- Bulk matchday entry
const kickVal = (v) => (v ? (v.includes("T") ? v.slice(0, 16) : `${v}T00:00`) : "");
const rowFromFixture = (f) => ({ h: f.home_score ?? "", a: f.away_score ?? "", status: f.status || "SCHEDULED", k: kickVal(f.scheduled_date) });
const INPUT = "w-11 text-center bg-[#0a0a0a] border rounded-md px-1 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60 disabled:opacity-40";

function BulkMatchdayPanel({ comp, byMd, mds, reload }) {
  const [md, setMd] = useState(mds[0]);
  const fixtures = byMd[md] || [];
  const [rows, setRows] = useState({});
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows(Object.fromEntries((byMd[md] || []).map((f) => [f.id, rowFromFixture(f)])));
    setErrs({});
  }, [md, comp]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (id, k, v) => setRows((r) => ({ ...r, [id]: { ...r[id], [k]: v } }));
  const [allK, setAllK] = useState("");
  const fillAll = () => {
    if (!allK) { toast.error("Pick a date and time first."); return; }
    setRows((r) => Object.fromEntries(fixtures.map((f) => [f.id, { ...(r[f.id] || rowFromFixture(f)), k: allK }])));
    toast.success(`Kick-off applied to ${fixtures.length} fixtures — review and Save`);
  };
  const save = async () => {
    const items = fixtures.map((f) => {
      const r = rows[f.id] || rowFromFixture(f);
      const played = r.status === "PLAYED";
      const item = { fixture_id: f.id, status: r.status,
        home_score: played && r.h !== "" ? Number(r.h) : null,
        away_score: played && r.a !== "" ? Number(r.a) : null };
      if (r.k !== kickVal(f.scheduled_date)) item.scheduled_date = r.k || null;
      return item;
    });
    setBusy(true); setErrs({});
    try {
      const { data } = await api.patch(`/admin/competitions/${comp.id}/fixtures/bulk`, { items });
      toast.success(`Matchday ${md}: ${data.updated} fixtures saved — standings refreshed`);
      await reload();
    } catch (e) {
      const list = e?.response?.data?.detail?.errors || [];
      setErrs(Object.fromEntries(list.map((x) => [x.fixture_id, x.message])));
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 space-y-3 border-[#E5B842]/40" data-testid="bulk-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold">Μαζική καταχώρηση · Bulk entry</div>
        <select data-testid="bulk-matchday-select" value={md} onChange={(e) => setMd(Number(e.target.value))}
          className="bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1.5 text-xs font-mono uppercase text-slate-200">
          {mds.map((m) => <option key={m} value={m}>{`Matchday ${m}`}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 flex-wrap rounded-md border border-[#E5B842]/20 bg-[#0a0a0a] px-3 py-2" data-testid="bulk-fill-kickoff">
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Ημερομηνία/ώρα για ΟΛΗ την αγωνιστική</span>
        <input type="datetime-local" value={allK} onChange={(e) => setAllK(e.target.value)} data-testid="bulk-fill-kickoff-input"
          className="bg-[#050505] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1 text-[11px] font-mono text-slate-200 [color-scheme:dark]" />
        <button onClick={fillAll} data-testid="bulk-fill-kickoff-apply"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase border border-[#E5B842]/50 text-[#E5B842] hover:bg-[#E5B842]/10" style={{ transition: "background-color 0.15s ease" }}>
          <CalendarDays className="w-3.5 h-3.5" />Apply to all
        </button>
      </div>
      <div className="divide-y divide-[rgba(212,175,55,0.1)]">
        {fixtures.map((f) => <BulkRow key={f.id} f={f} r={rows[f.id] || rowFromFixture(f)} set={set} err={errs[f.id]} />)}
      </div>
      <div className="flex justify-end">
        <GoldButton onClick={save} disabled={busy} data-testid="bulk-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Save matchday {md}
        </GoldButton>
      </div>
    </Card>
  );
}

function BulkRow({ f, r, set, err }) {
  const off = r.status !== "PLAYED";
  const bc = err ? "border-rose-500/70" : "border-[rgba(212,175,55,0.22)]";
  const num = (k) => (e) => set(f.id, k, e.target.value.replace(/[^0-9-]/g, ""));
  return (
    <div className="py-2" data-testid={`bulk-row-${f.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end text-right"><span className="truncate text-sm text-slate-100">{f.home.short_name || f.home.name}</span><ClubCrest club={f.home} size={22} /></div>
        <input data-testid={`bulk-home-${f.id}`} value={r.h} onChange={num("h")} inputMode="numeric" disabled={off} className={`${INPUT} ${bc}`} />
        <span className="text-slate-600">–</span>
        <input data-testid={`bulk-away-${f.id}`} value={r.a} onChange={num("a")} inputMode="numeric" disabled={off} className={`${INPUT} ${bc}`} />
        <div className="flex items-center gap-2 flex-1 min-w-0"><ClubCrest club={f.away} size={22} /><span className="truncate text-sm text-slate-100">{f.away.short_name || f.away.name}</span></div>
        <select data-testid={`bulk-status-${f.id}`} value={r.status} onChange={(e) => set(f.id, "status", e.target.value)}
          className="shrink-0 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1.5 text-[11px] font-mono uppercase text-slate-200">
          {["SCHEDULED", "PLAYED", "POSTPONED", "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="datetime-local" data-testid={`bulk-kickoff-${f.id}`} value={r.k} onChange={(e) => set(f.id, "k", e.target.value)}
          title="Kick-off" className="shrink-0 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-md px-2 py-1 text-[11px] font-mono text-slate-200 [color-scheme:dark]" />
      </div>
      {err && <div className="text-[11px] text-rose-300 text-center mt-1" data-testid={`bulk-error-${f.id}`}>{err}</div>}
    </div>
  );
}

function ScoresTab({ comp, reload }) {
  const [bulk, setBulk] = useState(false);
  const byMd = {};
  comp.fixtures.forEach((f) => { (byMd[f.matchday] = byMd[f.matchday] || []).push(f); });
  const mds = Object.keys(byMd).map(Number).sort((x, y) => x - y);
  if (comp.fixtures.length === 0) return <div data-testid="comp-tab-scores"><Empty label="Generate the schedule first to enter scores." /></div>;
  return (
    <div className="space-y-5" data-testid="comp-tab-scores">
      <div className="flex justify-end items-center gap-2 flex-wrap">
        <MatchdayShareBar comp={comp} fixtures={comp.fixtures} standings={comp.standings} testid="admin-matchday-share" />
        <LeadersShareButton comp={comp} standings={comp.standings} testid="admin-leaders-share" />
        <WeeklyPackButton comp={comp} fixtures={comp.fixtures} standings={comp.standings} testid="admin-weekly-pack" />
        <button onClick={() => setBulk((b) => !b)} data-testid="bulk-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase border ${bulk ? "bg-[#E5B842] text-black border-[#E5B842]" : "border-[#E5B842]/40 text-[#E5B842] hover:bg-[#E5B842]/10"}`}
          style={{ transition: "background-color 0.15s ease" }}>
          <Layers className="w-3.5 h-3.5" />{bulk ? "Κλείσιμο μαζικής" : "Μαζική καταχώρηση αγωνιστικής"}
        </button>
      </div>
      {bulk && <BulkMatchdayPanel comp={comp} byMd={byMd} mds={mds} reload={reload} />}
      {mds.map((md) => (
        <div key={md} data-testid={`scores-matchday-${md}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-2">Matchday {md}</div>
          <Card className="divide-y divide-[rgba(212,175,55,0.1)]">
            {byMd[md].map((f) => <ScoreRow key={f.id} comp={comp} f={f} reload={reload} />)}
          </Card>
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- Standings tab
function StandingsTab({ comp }) {
  if (comp.clubs.length === 0) return <div data-testid="comp-tab-standings"><Empty label="Add clubs to see the standings table." /></div>;
  const cols = [["P", "P"], ["W", "W"], ["D", "D"], ["L", "L"], ["GF", "GF"], ["GA", "GA"], ["GD", "GD"], ["Pts", "Pts"]];
  return (
    <div data-testid="comp-tab-standings" className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="standings-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono border-b border-[rgba(212,175,55,0.2)]">
            <th className="text-left py-2 pl-2 pr-2">#</th>
            <th className="text-left py-2 pr-2">Club</th>
            {cols.map(([k, l]) => <th key={k} className="text-center py-2 px-1.5">{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {comp.standings.map((r) => (
            <tr key={r.club_id} className="border-b border-[rgba(212,175,55,0.08)]" data-testid={`standings-row-${r.club_id}`}>
              <td className="py-2 pl-2 pr-2 font-mono text-slate-400">{r.rank}</td>
              <td className="py-2 pr-2"><div className="flex items-center gap-2 min-w-0"><ClubCrest club={r} size={22} /><span className="truncate text-slate-100">{r.name}</span></div></td>
              {cols.map(([k]) => <td key={k} className={`text-center py-2 px-1.5 tabular-nums ${k === "Pts" ? "font-bold text-[#E5B842]" : "text-slate-300"}`}>{k === "GD" ? (r.GD > 0 ? `+${r.GD}` : r.GD) : r[k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ----------------------------------------------------------------- Detail view
const TABS = [
  { key: "clubs", label: "Clubs", icon: Users },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "scores", label: "Scores", icon: Target },
  { key: "standings", label: "Standings", icon: ListOrdered },
];

function DetailView({ id }) {
  const nav = useNavigate();
  const [comp, setComp] = useState(null);
  const [tab, setTab] = useState("clubs");
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/competitions/${id}`); setComp(data); }
    catch (e) { toast.error(apiErr(e)); nav("/admin/competitions"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!comp) return <Loader label="Loading competition…" />;

  return (
    <div data-testid="admin-competition-detail">
      <Link to="/admin/competitions" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842] mb-4" data-testid="comp-back"><ArrowLeft className="w-3.5 h-3.5" /> All competitions</Link>
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none break-words">{comp.name}</h1>
          <StatusPill status={comp.status} />
        </div>
        <p className="text-slate-400 mt-2 text-sm font-mono">{comp.season_label} · single round-robin · scores informational only</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="comp-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`comp-tab-btn-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase tracking-wide ${on ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "clubs" && <ClubsTab comp={comp} reload={load} />}
      {tab === "schedule" && <ScheduleTab comp={comp} reload={load} />}
      {tab === "scores" && <ScoresTab comp={comp} reload={load} />}
      {tab === "standings" && <StandingsTab comp={comp} />}
    </div>
  );
}

export default function AdminCompetitions() {
  const { id } = useParams();
  return <AdminLayout>{id ? <DetailView id={id} /> : <ListView />}</AdminLayout>;
}
